import { GUNSHOT_EARSHOT, crimePoints } from '../../heat/heatModel';
import { resolveHit } from '../combat';
import { PERSON_HP, WEAPONS, WEAPON_ORDER, type WeaponId } from '../weaponData';

/**
 * The price tag's numbers (Sid's back room, docs/WEAPONS.md §6): every bar is read off the roster in
 * weaponData.ts and the heat model, never typed in. Pure, so the tests can hold the mapping to the data.
 *
 *  - Hitting power: damage of one blow / one shot at close range (all a shotgun's pellets), against a
 *    person's 100; the text is how many it takes to put somebody down (resolveHit, the real rule).
 *  - Reach: a melee weapon's reach or a gun's range, on a square-root scale over the roster's longest
 *    (a straight scale leaves every blade and club as an invisible sliver next to the .38).
 *  - Speed: swings / shots a second, over the roster's quickest.
 *  - Rounds (guns only): rounds in the gun over the roster's biggest, with the reload and what you can carry.
 *  - Heat: the points one use scores with a witness about (heatModel.crimePoints), over the loudest.
 */

export type StatKey = 'damage' | 'reach' | 'speed' | 'rounds' | 'heat';

export interface StatBar {
  key: StatKey;
  label: string;
  /** 0..1 bar fill. */
  value: number;
  /** The number behind it, in words. */
  text: string;
}

export interface WeaponCard {
  id: WeaponId;
  name: string;
  blurb: string;
  gun: boolean;
  /** Can it kill (a cosh or fists only ever put people down). */
  lethal: boolean;
  lethalText: string;
  bars: StatBar[];
  /** What else it's good for (break-ins, bodywork). */
  notes: string[];
}

/** One use's damage to a person at close range (a shotgun: every pellet). */
export function hitDamage(id: WeaponId): number {
  const w = WEAPONS[id];
  return w.firearm ? w.firearm.damage * w.firearm.pellets : w.melee.damage;
}

/** Blows / shots from a fresh 100 until somebody goes down (the combat rule, resolveHit). */
export function hitsToDrop(id: WeaponId): number {
  const dmg = hitDamage(id);
  const lethal = WEAPONS[id].firearm ? true : WEAPONS[id].melee.lethal;
  let hp = PERSON_HP;
  for (let n = 1; n <= 20; n++) {
    const r = resolveHit(hp, dmg, lethal);
    if (r.knocked) return n;
    hp = r.hp;
  }
  return 20;
}

const reachOf = (id: WeaponId): number => WEAPONS[id].firearm?.range ?? WEAPONS[id].melee.reach;
const intervalOf = (id: WeaponId): number => WEAPONS[id].firearm?.fireInterval ?? WEAPONS[id].melee.swingTime;
/** A witness who saw it all (one civilian close by: witness factor 1). */
const SEEN = { police: 0, civilians: 3 };

/** Heat points one use scores: a blow on somebody is an assault; a shot is a discharge (heard) plus a shooting. */
export function heatPerUse(id: WeaponId): number {
  if (WEAPONS[id].firearm) return crimePoints('firearmDischarge', 4, SEEN) + crimePoints('shooting', 5, SEEN);
  return crimePoints('assault', 3, SEEN);
}

const ROSTER = WEAPON_ORDER.filter((id) => id !== 'fists');
const MAX_REACH = Math.max(...ROSTER.map(reachOf));
const MIN_INTERVAL = Math.min(...ROSTER.map(intervalOf));
const MAX_CAPACITY = Math.max(...ROSTER.map((id) => WEAPONS[id].firearm?.capacity ?? 0));
const MAX_HEAT = Math.max(...ROSTER.map(heatPerUse));

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
const fmt = (v: number): string => (v < 1 ? Math.round(v * 100) / 100 : Math.round(v * 10) / 10).toString();
const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

export function weaponCard(id: WeaponId): WeaponCard {
  const w = WEAPONS[id];
  const f = w.firearm;
  const drop = hitsToDrop(id);
  const bars: StatBar[] = [
    {
      key: 'damage',
      label: 'Hitting power',
      value: clamp01(hitDamage(id) / PERSON_HP),
      text: `Puts a man down in ${plural(drop, f ? 'shot' : 'blow', f ? 'shots' : 'blows')}`,
    },
    {
      key: 'reach',
      label: f ? 'Range' : 'Reach',
      value: clamp01(Math.sqrt(reachOf(id) / MAX_REACH)),
      text: f ? `Carries ${reachOf(id)} m, hits hardest inside ${f.falloffStart} m` : `Reaches ${fmt(reachOf(id))} m`,
    },
    {
      key: 'speed',
      label: 'Speed',
      value: clamp01(MIN_INTERVAL / intervalOf(id)),
      text: f ? `A shot every ${fmt(f.fireInterval)} s` : `${fmt(w.melee.swingTime)} s a swing`,
    },
  ];
  if (f) {
    bars.push({
      key: 'rounds',
      label: 'Rounds',
      value: clamp01(f.capacity / MAX_CAPACITY),
      text: `${f.capacity} loaded, ${fmt(f.reloadTime)} s to reload, ${f.maxReserve} spare on you`,
    });
  }
  bars.push({
    key: 'heat',
    label: 'Heat',
    value: clamp01(heatPerUse(id) / MAX_HEAT),
    text: f ? `Heard ${GUNSHOT_EARSHOT} m off: somebody always rings it in` : 'Only if somebody sees you use it',
  });
  const lethal = f ? true : w.melee.lethal;
  const notes: string[] = [];
  if (w.breakInScale < 1) notes.push(`Opens a locked car in ${Math.round(w.breakInScale * 100)}% of the time.`);
  if (!f && w.melee.vehicleDamage > 0) notes.push('Dents bodywork.');
  if (f) notes.push('Illegal. The police keep it if you are nicked.');
  return {
    id,
    name: w.name,
    blurb: w.blurb,
    gun: !!f,
    lethal,
    lethalText: lethal ? 'Can kill' : 'Won’t kill: puts them down, not in the ground',
    bars,
    notes,
  };
}

